import React, { createContext, useContext, useState, ReactNode } from 'react';
import { authApi } from '../services/api';

interface User {
  username: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 앱 시작 시 토큰 검증
  React.useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authApi.getToken();
        if (token && !authApi.isTokenExpired()) {
          // 토큰이 유효하면 서버에서 검증
          const response = await authApi.verify();
          if (response.success) {
            setIsAuthenticated(true);
            setUser(response.user);
          } else {
            // 토큰이 유효하지 않으면 제거
            authApi.logout();
          }
        }
      } catch (error) {
        console.error('토큰 검증 에러:', error);
        authApi.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (user: User) => {
    setIsAuthenticated(true);
    setUser(user);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('로그아웃 에러:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 